'use client';

import { Container } from '..';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { isPathAllowedForRegion } from 'lib/region/regionPathAccess';
import { H2 } from './styleConstants';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { useTranslations } from 'next-intl';
import { CustomImage, ButtonV2 } from 'components/shared';
import { CaretUp } from 'assets/icons';
import styles from './BigPartnersSection.module.css';

const v1BasePath = 'web_v2/homepage-v3/v1';

export const BigPartnersSection = ({ className }: { className?: string }) => {
  const t = useTranslations('partners');
  const { width } = useWindowSize();
  const isMobile = (width ?? 0) < 768;

  const partnersData = [
    {
      title: 'Diesel',
      description: t.rich('bigPartners.diesel.description', {
        b: (chunks) => <b>{chunks}</b>,
      }),
      image: isMobile
        ? `${v1BasePath}/mobile/featured/diesel-uh-ring-resp.png`
        : `${v1BasePath}/desktop/featured/diesel-uh-ring-v2.png`,
      ctaSecondary: {
        variant: 'transparentDark' as const,
        label: t('bigPartners.learnMore'),
        href: {
          pathname: '/diesel-ultrahuman-ring/',
        },
        'data-buttontype': 'dieselRing_features_products_buy_button',
      },
    },
    {
      title: 'Clue',
      description: t.rich('bigPartners.clue.description', {
        b: (chunks) => <b>{chunks}</b>,
      }),
      image: isMobile
        ? `web_v2/partners/clue-card.png`
        : `web_v2/partners/clue-card.png`,
      ctaSecondary: {
        variant: 'transparentDark' as const,
        label: t('bigPartners.learnMore'),
        href: {
          pathname: '/ultrahuman-x-clue/',
        },
        'data-buttontype': 'clue_features_products_learn_more_button',
      },
    },
  ];

  // main gates the cards on region: a partner whose page is excluded there
  // (Clue in US/PR) must not be advertised.
  const { region } = useRegionLocale();

  return (
    <section className={`${styles.root}${className ? ` ${className}` : ''}`}>
      <Container className={styles.container}>
        <H2 className={styles.heading}>{t('bigPartners.title')}</H2>
        <div className={styles.cardsGrid}>
          {partnersData
            .filter((partner) =>
              isPathAllowedForRegion(partner.ctaSecondary.href.pathname, region),
            )
            .map((partner, index) => (
            <div className={styles.partnerCard} key={index}>
              <div className={styles.imageWrap}>
                <div className={styles.insetBorder} />
                <CustomImage
                  className={styles.partnerImage}
                  src={partner.image}
                  alt={partner.title}
                  fill
                  decoding="async"
                  sizes="(max-width: 768px) 100vw, (max-width: 1440px) 50vw, 33vw"
                />
              </div>
              <div className={styles.partnerContent}>
                <h3 className={styles.partnerTitle}>{partner.title}</h3>
                <p className={styles.partnerDescription}>{partner.description}</p>
                <div className={styles.partnerActions}>
                  <ButtonV2
                    variant={'flat'}
                    href={partner.ctaSecondary.href}
                    data-buttontype={partner.ctaSecondary['data-buttontype']}
                    style={{ padding: '0' }}
                  >
                    {partner.ctaSecondary.label}{' '}
                    <CaretUp
                      style={{
                        transform: 'rotate(90deg) translateY(-1px)',
                        height: '8px',
                        marginLeft: '4px',
                      }}
                      fill={'#000'}
                    />
                  </ButtonV2>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};
